import { useEffect, useRef } from "react"
import { Navigate, Outlet, useLocation } from "react-router-dom"
import type { Role } from "shared"

import { authClient } from "@/lib/api/auth"
import { useSession as useAppSession } from "@/store/session"

/**
 * Route guard for react-router v6+ nested routes: wrap protected `<Route>`s
 * in `<Route element={<RequireAuth />}>` and they render as its `<Outlet />`.
 * better-auth's `useSession()` is the source of truth for whether the
 * session cookie is valid (it re-fetches `/api/auth/get-session`), not the
 * zustand store — that store only gets populated by the login flow itself,
 * so it's empty on a hard refresh even with a valid session cookie.
 *
 * better-auth also refetches on every window/tab focus, reporting
 * `isPending: true` again for each refetch while there's no cached session
 * (see `getSessionAtom` in better-auth's `session-atom.ts`) — gating on
 * `isPending` unconditionally would unmount `<Outlet />` on every focus
 * refetch of an expired/missing session. `hasLoadedOnce` limits the loading
 * gate to the very first resolution only.
 *
 * Also owns fetching `GET /api/otk/subject` into the app store once
 * `user.subjectId` is known — every protected route mounts under this
 * guard, so it's the one place that can load the subject profile exactly
 * once per session rather than each page fetching it independently.
 */
export function RequireAuth() {
  const { data, isPending } = authClient.useSession()
  const location = useLocation()
  const appUser = useAppSession((s) => s.user)
  const subject = useAppSession((s) => s.subject)
  const setSession = useAppSession((s) => s.setSession)
  const fetchSubject = useAppSession((s) => s.fetchSubject)
  const hasLoadedOnce = useRef(false)
  if (!isPending) hasLoadedOnce.current = true

  useEffect(() => {
    if (data?.user && !appUser) {
      setSession({
        user: {
          id: data.user.id,
          name: data.user.name,
          email: data.user.email,
          rnokpp: data.user.rnokpp,
          subjectId: data.user.subjectId,
          roles: (data.user.roles as Role[] | undefined) ?? [],
        },
      })
    }
  }, [data, appUser, setSession])

  useEffect(() => {
    if (!appUser?.subjectId || subject) return
    fetchSubject().catch(() => {
      // Swallowed: routes that don't need the subject profile (e.g.
      // /connect-subject right after a fresh connect) shouldn't be
      // blocked by this failing. Pages that do need it (SubjectProfile)
      // already handle SUBJECT_NOT_CONNECTED/load errors themselves.
    })
  }, [appUser?.subjectId, subject, fetchSubject])

  if (isPending && !hasLoadedOnce.current) return null

  if (!data) {
    return <Navigate replace state={{ from: location }} to="/login" />
  }

  return <Outlet />
}
